// File: webelieve-frontend/src/features/intake/components/IntakeQueue.tsx
// M7.30: Refactored to show supervisory operational queues with drill-down
// into real lead cohorts from the backend cohort API.

import React, { useState, useCallback } from 'react';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  DocumentTextIcon,
  EyeIcon,
  UserGroupIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { SupervisoryActionableQueue, CohortLead, CohortResult } from '../api/edgeflowApi';
import { edgeflowApi } from '../api/edgeflowApi';

interface IntakeQueueProps {
  actionableQueues: SupervisoryActionableQueue[];
  onRefresh: () => void;
  /** Externally requested drill-down queue (e.g., from MetricsOverview card click) */
  externalDrillQueue?: string | null;
  /** Called when external drill has been handled so parent can clear the request */
  onExternalDrillHandled?: () => void;
}

const QUEUE_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  high_urgency_unreviewed: ExclamationTriangleIcon,
  workflow_failures: ExclamationTriangleIcon,
  missing_conflict_check: ShieldExclamationIcon,
  retainer_unsigned: DocumentTextIcon,
  missing_documents: DocumentTextIcon,
  stuck_in_stage: ClockIcon,
  stale_leads: ClockIcon,
  review_required: EyeIcon,
};

const SEVERITY_COLORS: Record<string, string> = {
  urgent: 'border-l-red-500 bg-red-50 dark:bg-red-900/10',
  elevated: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/10',
  attention: 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10',
  info: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10',
};

const PAGE_SIZE = 10;

const IntakeQueue: React.FC<IntakeQueueProps> = ({ actionableQueues, onRefresh, externalDrillQueue, onExternalDrillHandled }) => {
  const [expandedQueue, setExpandedQueue] = useState<string | null>(null);
  const [cohortData, setCohortData] = useState<CohortResult | null>(null);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Handle external drill-down request from MetricsOverview cards
  React.useEffect(() => {
    if (externalDrillQueue && externalDrillQueue !== expandedQueue) {
      handleQueueClick(externalDrillQueue);
      onExternalDrillHandled?.();
    }
  }, [externalDrillQueue]);

  const handleQueueClick = useCallback(async (queueName: string) => {
    if (expandedQueue === queueName) {
      setExpandedQueue(null);
      setCohortData(null);
      return;
    }

    setExpandedQueue(queueName);
    setCohortLoading(true);
    try {
      const data = await edgeflowApi.getCohort(queueName, { limit: PAGE_SIZE });
      setCohortData(data);
    } catch (err) {
      console.error('Failed to load cohort:', err);
      setCohortData(null);
    } finally {
      setCohortLoading(false);
    }
  }, [expandedQueue]);

  const handleLoadMore = useCallback(async () => {
    if (!expandedQueue || !cohortData) return;
    const currentCount = cohortData.leads.length;
    if (currentCount >= cohortData.total) return;

    setLoadingMore(true);
    try {
      const nextPage = await edgeflowApi.getCohort(expandedQueue, {
        limit: PAGE_SIZE,
        offset: currentCount,
      });
      setCohortData({
        ...cohortData,
        leads: [...cohortData.leads, ...nextPage.leads],
      });
    } catch (err) {
      console.error('Failed to load more cohort data:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [expandedQueue, cohortData]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (actionableQueues.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Attention Queues</h2>
          <button onClick={onRefresh} className="text-sm text-lex-accent hover:text-lex-accent-hover flex items-center gap-1">
            <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <UserGroupIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm font-medium">All clear</p>
          <p className="text-xs">No items need attention right now</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Attention Queues</h2>
        <button onClick={onRefresh} className="text-sm text-lex-accent hover:text-lex-accent-hover flex items-center gap-1">
          <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="space-y-2">
        {actionableQueues.map((q) => {
          const Icon = QUEUE_ICONS[q.queue_name] || UserGroupIcon;
          const isExpanded = expandedQueue === q.queue_name;

          return (
            <div key={q.queue_name} className="rounded-lg overflow-hidden">
              {/* Queue header */}
              <button
                onClick={() => handleQueueClick(q.queue_name)}
                className={`w-full p-3 border-l-4 rounded-lg text-left transition-all hover:shadow-md ${SEVERITY_COLORS[q.severity] || SEVERITY_COLORS.info}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {q.description}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    {q.count}
                  </span>
                </div>
              </button>

              {/* Expanded cohort drill-down */}
              {isExpanded && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-t-0 rounded-b-lg p-3">
                  {cohortLoading ? (
                    <div className="text-center py-4">
                      <div className="w-5 h-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="text-xs text-gray-500 mt-2">Loading leads...</p>
                    </div>
                  ) : cohortData && cohortData.leads.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {cohortData.leads.map((lead) => (
                        <div key={lead.client_id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {lead.client_name}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              <span>{lead.current_stage || lead.status}</span>
                              {lead.practice_area && <span>· {lead.practice_area}</span>}
                              <span>· {formatDate(lead.lead_created_at)}</span>
                            </div>
                          </div>
                          {lead.recommended_next_action && (
                            <span className="text-xs text-lex-accent ml-2 flex-shrink-0">
                              {lead.recommended_next_action.length > 30
                                ? lead.recommended_next_action.slice(0, 30) + '...'
                                : lead.recommended_next_action}
                            </span>
                          )}
                        </div>
                      ))}
                      {cohortData.total > cohortData.leads.length && (
                        <div className="text-center pt-2 space-y-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Showing {cohortData.leads.length} of {cohortData.total}
                          </p>
                          <button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className="px-3 py-1 text-xs font-medium text-lex-accent bg-lex-accent-soft hover:bg-lex-state-hover rounded transition-colors disabled:opacity-50"
                          >
                            {loadingMore ? 'Loading...' : `Load more (${Math.min(PAGE_SIZE, cohortData.total - cohortData.leads.length)} more)`}
                          </button>
                        </div>
                      )}
                      {cohortData.total > 0 && cohortData.leads.length >= cohortData.total && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-1">
                          All {cohortData.total} leads loaded
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-3">No leads in this queue</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntakeQueue;
